<template>
  <div
    v-if="isVisible"
    ref="modalElement"
    class="modal estimate-pdf-preview-modal"
    tabindex="-1"
    role="dialog"
    aria-modal="true"
    aria-labelledby="estimatePdfPreviewModalLabel"
    @keydown.esc="close"
  >
    <div class="modal-dialog modal-xl" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <div>
            <h5 id="estimatePdfPreviewModalLabel" class="modal-title">見積書PDFプレビュー</h5>
            <div class="preview-filename text-muted">{{ filename }}</div>
          </div>
          <button type="button" class="btn-close" aria-label="閉じる" @click="close"></button>
        </div>

        <div class="modal-body">
          <div
            v-if="pageImages.length"
            class="page-list"
            :class="{ 'is-landscape': orientation === 'landscape' }"
          >
            <img
              v-for="(src, index) in pageImages"
              :key="index"
              :src="src"
              class="page-image"
              :alt="`見積書 ${index + 1}ページ目`"
            >
          </div>
          <iframe
            v-else-if="objectUrl && canEmbedPdf"
            :src="objectUrl"
            class="pdf-frame"
            title="見積書PDFプレビュー"
          ></iframe>
          <div v-else class="preview-unavailable">
            {{ objectUrl
              ? 'このブラウザの設定では画面内にPDFを表示できません。ダウンロードしてご確認ください。'
              : 'PDFを表示できません。' }}
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="close">閉じる</button>
          <button type="button" class="btn btn-primary" :disabled="!objectUrl" @click="download">
            ダウンロード
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { nextTick, onBeforeUnmount, ref } from 'vue';

const isVisible = ref(false);
const filename = ref('見積書.pdf');
const objectUrl = ref('');
const pageImages = ref([]);
const orientation = ref('portrait');
const modalElement = ref(null);
// 保存済みPDFはページ画像を持たないため、ブラウザのPDFビューアで表示する。
const canEmbedPdf = navigator.pdfViewerEnabled !== false;

const revokeObjectUrl = () => {
  if (!objectUrl.value) return;
  URL.revokeObjectURL(objectUrl.value);
  objectUrl.value = '';
};

const show = async ({ blob, filename: nextFilename, pageImages: nextPageImages, orientation: nextOrientation }) => {
  revokeObjectUrl();
  filename.value = nextFilename || '見積書.pdf';
  pageImages.value = nextPageImages || [];
  orientation.value = nextOrientation || 'portrait';
  objectUrl.value = URL.createObjectURL(blob);
  isVisible.value = true;
  await nextTick();
  modalElement.value?.focus();
};

const close = () => {
  isVisible.value = false;
  pageImages.value = [];
  revokeObjectUrl();
};

const download = () => {
  if (!objectUrl.value) return;
  const link = document.createElement('a');
  link.href = objectUrl.value;
  link.download = filename.value;
  document.body.appendChild(link);
  link.click();
  link.remove();
};

onBeforeUnmount(revokeObjectUrl);

defineExpose({ show, close });
</script>

<style scoped>
.estimate-pdf-preview-modal {
  /* 検索セレクトのクリアボタン（z-index: 1100）より前面に出す */
  z-index: 1200;
  display: block;
  padding: 12px;
  background-color: rgba(0, 0, 0, 0.62);
}

.modal-dialog {
  width: min(1180px, calc(100vw - 24px));
  max-width: none;
  height: calc(100vh - 24px);
  margin: 0 auto;
}

.modal-content {
  height: 100%;
}

.modal-header,
.modal-footer {
  flex: 0 0 auto;
}

.modal-body {
  min-height: 0;
  padding: 0;
  overflow: hidden;
  background: #555;
}

.preview-filename {
  max-width: min(70vw, 760px);
  overflow: hidden;
  font-size: 0.78rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.page-list {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  height: 100%;
  padding: 16px;
  overflow-y: auto;
}

.page-image {
  display: block;
  width: 100%;
  max-width: 820px;
  height: auto;
  background: #fff;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.45);
}

.page-list.is-landscape .page-image {
  max-width: 1120px;
}

.pdf-frame {
  display: block;
  width: 100%;
  height: 100%;
  min-height: 520px;
  border: 0;
  background: #fff;
}

.preview-unavailable {
  display: grid;
  min-height: 320px;
  padding: 16px;
  place-items: center;
  color: #fff;
  text-align: center;
}

@media (max-width: 767px) {
  .estimate-pdf-preview-modal {
    padding: 0;
  }

  .modal-dialog {
    width: 100vw;
    height: 100vh;
  }

  .modal-content {
    border: 0;
    border-radius: 0;
  }

  .pdf-frame {
    min-height: 380px;
  }

  .page-list {
    padding: 8px;
  }
}
</style>
